import type { LucideIcon } from 'lucide-react';
import {
  Sparkles, Star, Puzzle, Briefcase,
  Heart, AlertTriangle, Laugh, BookOpen, MapPin,
  Check, Ban, Lightbulb, Utensils, Coffee,
  Camera, MessageCircle,
} from 'lucide-react';
import type { DiagnosisResultData } from '@/data/diagnosisResults/types';
import { getDiagnosisThemeVars } from '@/data/diagnosisResults/theme';
import CharacterHeroImage from './CharacterHeroImage';
import { diagnosisResultStyles } from './diagnosisResultStyles';

export type { DiagnosisResultData };

export interface DiagnosisResultViewProps {
  data: DiagnosisResultData;
  appStoreHref?: string;
  googlePlayHref?: string;
}

const ICONS: Record<string, LucideIcon> = { utensils: Utensils, coffee: Coffee, camera: Camera, msg: MessageCircle };

/* accent色 → 微グラデのマップ */
const GRAD_MAP: Record<string, string> = {
  "var(--brand)":  "var(--gradBrand)",
  "var(--green)":  "var(--gradGreen)",
  "var(--orange)": "var(--gradOrange)",
  "var(--pink)":   "var(--gradPink)",
  "var(--blue)":   "var(--gradBlue)",
};

function Rarity({ value, max = 5 }) {
  return (
    <span className="dr-stars">
      {Array.from({ length: max }).map((_, i) => (
        <Star key={i} size={20} color="var(--brand)"
          fill={i < value ? "var(--brand)" : "none"} strokeWidth={1.6} />
      ))}
      <span style={{ marginLeft: 6, color: "var(--body)", fontWeight: 700, fontSize: 14 }}>
        （{value}/{max}）
      </span>
    </span>
  );
}

function InfoRow({ icon: Icon, accent, label, children }) {
  return (
    <div className="dr-info-row">
      <Icon size={16} color={accent} />
      <span className="dr-info-label">{label}：</span>
      <span style={{ fontSize: 13 }}>{children}</span>
    </div>
  );
}

function SectionCard({ icon: Icon, title, badge, accent, children }) {
  const gradStyle = {
    backgroundImage: GRAD_MAP[accent] || accent,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };
  return (
    <div className="dr-card">
      <div className="dr-card-head">
        <Icon size={20} color={accent} strokeWidth={2.2} />
        <h3 style={gradStyle}>
          {title}
          {badge && <span className="badge"> （{badge}）</span>}
        </h3>
      </div>
      <div className="dr-card-body">{children}</div>
    </div>
  );
}

function BulletItem({ lead, text, color }) {
  return (
    <li className="dr-bullet-item">
      <span style={{ color, fontSize: 13, lineHeight: "20px" }}>●</span>
      <span>
        <span style={{ color, fontWeight: 800 }}>{lead}</span>
        {text && <span style={{ color: "var(--sub)" }}>：{text}</span>}
      </span>
    </li>
  );
}

function ManualRow({ icon: Icon, color, label, text }) {
  return (
    <div className="dr-manual-row">
      <Icon size={17} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
      <span>
        <span style={{ fontWeight: 800, color }}>{label}</span>
        <span style={{ color: "var(--body)" }}>：{text}</span>
      </span>
    </div>
  );
}

export default function DiagnosisResultView({ data, appStoreHref, googlePlayHref }: DiagnosisResultViewProps) {
  const themeVars = getDiagnosisThemeVars(data.code);
  return (
    <>
      <style>{diagnosisResultStyles}</style>
      <div className="dr-root" style={themeVars}>
        <div className="dr-container">
          <main className="dr-panel">

            {/* ヒーロー */}
            <div className="dr-hero">
              <div className="dr-hero-info">
                <h1 className="dr-title">
                  {data.no}. {data.code}型<br />
                  <span className="dr-title-sub">（{data.name}）</span>
                </h1>
                <InfoRow icon={Sparkles} accent="var(--brand)" label="キャッチコピー">
                  <span style={{ color: "var(--ink)", fontWeight: 700 }}>{data.catchphrase}</span>
                </InfoRow>
                <InfoRow icon={Star} accent="#F2872E" label="レアリティ">
                  <Rarity value={data.rarity} />
                </InfoRow>
              </div>
              <div className="dr-char-wrap">
                <CharacterHeroImage src={data.characterImage} alt={data.name} />
              </div>
            </div>

            {/* 軸 × スタイル */}
            <div className="dr-two-col">
              <SectionCard icon={Puzzle} title="軸の組み合わせ" accent="var(--brand)">
                {data.axes.map((a) => (
                  <div key={a.key} className="dr-axis-row">
                    <span className="dr-axis-pill">{a.key}</span>
                    <span style={{ fontSize: 13, color: "var(--body)" }}>： {a.value}</span>
                  </div>
                ))}
              </SectionCard>
              <SectionCard icon={Briefcase} title="旅行スタイル" badge="style" accent="var(--brand)">
                <div className="dr-style-box">{data.style}</div>
              </SectionCard>
            </div>

            {/* 強み・注意点・あるある・説明書・相性・旅先 */}
            <div className="dr-section-grid">

              <SectionCard icon={Heart} title="強み" badge="str" accent="var(--green)">
                <ul className="dr-bullet-list">
                  {data.strengths.map((s, i) => (
                    <BulletItem key={i} lead={s.lead} text={s.text} color="var(--green)" />
                  ))}
                </ul>
              </SectionCard>

              <div className="dr-orange-card">
                <div className="dr-card-head" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <AlertTriangle size={20} color="var(--orange)" fill="var(--orange)" />
                  <h3 style={{
                    backgroundImage: "var(--gradOrange)",
                    WebkitBackgroundClip: "text", backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}>
                    注意点 <span className="badge">（wk）</span>
                  </h3>
                </div>
                <ul className="dr-bullet-list">
                  {data.weaknesses.map((w, i) => (
                    <BulletItem key={i} lead={w.lead} text={w.text} color="var(--orange)" />
                  ))}
                </ul>
              </div>

              <SectionCard icon={Laugh} title="旅あるある" badge="ara" accent="var(--brand)">
                <div
                  className={`dr-aaru-grid${data.aaru.length % 2 === 1 ? ' dr-aaru-grid--odd' : ''}`}
                >
                  {data.aaru.map((a, i) => {
                    const Ico = ICONS[a.icon] || Utensils;
                    return (
                      <div key={i} className="dr-aaru-item">
                        <span className="dr-aaru-icon" aria-hidden="true">
                          <Ico size={15} color="var(--brand)" strokeWidth={2.2} />
                        </span>
                        <span className="dr-aaru-text">{a.text}</span>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>

              <SectionCard icon={BookOpen} title="友達向け取扱説明書" badge="man" accent="var(--brand)">
                <ManualRow icon={Check}     color="var(--green)"  label="やると良い" text={data.manual.good} />
                <ManualRow icon={Ban}       color="#D9534F"       label="NG"         text={data.manual.ng} />
                <ManualRow icon={Lightbulb} color="var(--orange)" label="コツ"       text={data.manual.tips} />
              </SectionCard>

              <SectionCard icon={Heart} title="相性" badge="comp" accent="var(--pink)">
                <ManualRow icon={Check}         color="var(--green)"  label="相性が良い"   text={data.compatibility.good} />
                <ManualRow icon={AlertTriangle}  color="var(--orange)" label="衝突しやすい" text={data.compatibility.conflict} />
                <ManualRow icon={MessageCircle}  color="var(--blue)"   label="同行時のコツ" text={data.compatibility.tips} />
              </SectionCard>

              <SectionCard icon={MapPin} title="おすすめ旅先" badge="dest" accent="var(--blue)">
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[["国内", data.destinations.domestic], ["海外", data.destinations.overseas]].map(([label, text]) => (
                    <div key={label} style={{ display: "flex", gap: 8, fontSize: 13.5, lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 800, color: "var(--ink)", flexShrink: 0 }}>{label}：</span>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

            </div>
          </main>

          <div className="dr-cta-banner">
            <h3 className="dr-cta-title">あなたにぴったりの旅を見つけよう</h3>
            <p className="dr-cta-desc">{data.cta}</p>
            <div className="dr-cta-actions">
              {appStoreHref ? (
                <a
                  className="dr-cta-btn dr-cta-btn--primary"
                  href={appStoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="ri-apple-fill" aria-hidden />
                  App Storeでダウンロード
                </a>
              ) : (
                <button type="button" className="dr-cta-btn dr-cta-btn--primary">
                  <i className="ri-apple-fill" aria-hidden />
                  App Storeでダウンロード
                </button>
              )}
              {googlePlayHref ? (
                <a
                  className="dr-cta-btn dr-cta-btn--ghost"
                  href={googlePlayHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="ri-google-play-fill" aria-hidden />
                  Google Playでダウンロード
                </a>
              ) : (
                <button type="button" className="dr-cta-btn dr-cta-btn--ghost">
                  <i className="ri-google-play-fill" aria-hidden />
                  Google Playでダウンロード
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
